/**
 * RestoID — one-time-token error screen.
 * ---------------------------------------------------------------------------
 * Swapped in for `src/pages/OneTimeToken/Error`, whose only job is to read the
 * navigation state left by the token flow and hand it to an ErrorPage. Identical
 * to upstream except that it imports `./ErrorPage` directly rather than relying
 * on the `src/pages/ErrorPage` re-export shim.
 *
 * The state guard is upstream's, character for character — including the
 * `tFunctionKey` runtime check, which exists because the state is attacker-
 * reachable (it arrives through a navigate() the page itself performs after an
 * API error) and must not be trusted to actually hold i18n keys.
 */
import type { TFuncKey } from 'i18next';
import { useLocation } from 'react-router-dom';
import { define, object, optional, string, validate } from 'superstruct';

import ErrorPage from './ErrorPage';

// Runtime guard for i18n key strings while preserving TFuncKey typing at compile-time.
const tFunctionKey = define<TFuncKey>('TFuncKey', (value) => typeof value === 'string');

const stateGuard = object({
  title: optional(tFunctionKey),
  message: optional(tFunctionKey),
  errorMessage: optional(string()),
});

const OneTimeTokenError = () => {
  const { state } = useLocation();
  const [, parsed] = validate(state, stateGuard);

  return (
    <ErrorPage
      isNavbarHidden
      title={parsed?.title ?? 'error.invalid_link'}
      message={parsed?.message ?? 'error.invalid_link_description'}
      rawMessage={parsed?.errorMessage}
    />
  );
};

export default OneTimeTokenError;
